import { useRef, useState } from "react"

import { useTranslation } from "react-i18next"

import type { TenderHandoffPreview, TenderHandoffRecipient } from "@/api/tender-api"
import type { DirectorySearchResult } from "@/services/directory-search-service"

import { DirectorySearch } from "@/components/directory-search"
import { ArrowRightLeft, Crown, Eye, Loader2, Mail, Users } from "@/components/icons"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Label } from "@/components/ui/label"
import { Separator } from "@/components/ui/separator"
import { Textarea } from "@/components/ui/textarea"

import { PersonRow } from "./person-picker"
import { TenderSummaryEmailDialog } from "./tender-summary-email-dialog"
import {
  useHandoffTenderCase,
  usePreviewTenderHandoffEmail,
  useTenderHandoffCc,
  useTenderHandoffConfig,
} from "../hooks/use-tender-cases"
import { serverErrorMessage } from "../utils/server-error-message"

type TenderHandoffDialogProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  caseId: string
  handedOff?: boolean
  /** The Ansvarig recorded at creation or in Setup; seeds the main recipient. */
  responsible?: TenderHandoffRecipient | null
}

/** Hand-over needs a definite email; the directory may not carry one. */
function toHandoffRecipient(result: DirectorySearchResult): TenderHandoffRecipient {
  return { oid: result.oid, displayName: result.displayName, email: result.email ?? "" }
}

export function TenderHandoffDialog({
  open,
  onOpenChange,
  caseId,
  handedOff = false,
  responsible = null,
}: TenderHandoffDialogProps) {
  const { t } = useTranslation()
  const { data: handoffConfig } = useTenderHandoffConfig()
  const handoff = useHandoffTenderCase(caseId)
  const preview = usePreviewTenderHandoffEmail(caseId)

  // The message is prefilled with the default bid prompt and stays editable; it
  // replaces that prompt at the top of the hand-over email.
  const defaultMessage = t("tender.handoff.messageDefault")

  const [main, setMain] = useState<TenderHandoffRecipient | null>(null)
  const [cc, setCc] = useState<TenderHandoffRecipient[]>([])
  const [note, setNote] = useState(defaultMessage)
  const [error, setError] = useState<string | null>(null)
  const [warning, setWarning] = useState<string | null>(null)
  // Rendered email shown in the nested preview dialog; null while closed.
  const [previewData, setPreviewData] = useState<TenderHandoffPreview | null>(null)
  const [summaryOnly, setSummaryOnly] = useState(false)
  // Which (case, Ansvarig) pair the main recipient has already been seeded for.
  // Declared here, not next to the seeding block: reset() runs during render.
  const [seededFor, setSeededFor] = useState<string | null>(null)
  // Same, for the Cc list seeded from the case's Deltagare.
  const [ccSeededFor, setCcSeededFor] = useState<string | null>(null)
  // A handoff invalidates the case query. Keep its result visible while that
  // refresh changes `handedOff`, especially when the ownership transfer worked
  // but the notification email did not.
  const preserveHandoffResultRef = useRef(false)
  const [preserveHandoffResult, setPreserveHandoffResult] = useState(false)
  // Bumped on every reset (close, case change) so a preview request that was
  // started in an earlier dialog context is ignored when it completes. The
  // case page is reused across :caseId, so without this a delayed case-A
  // response could open A's email in B's dialog.
  const previewGenerationRef = useRef(0)

  const scopeGroup = handoffConfig?.userGroupId ?? undefined
  const selectedOids = [main?.oid, ...cc.map((r) => r.oid)].filter(Boolean) as string[]
  // Inputs freeze while either request is in flight so the preview (or the
  // sent email) always reflects exactly what was on screen when it was requested.
  const busy = handoff.isPending || preview.isPending
  const canPreview = !busy

  const reset = () => {
    previewGenerationRef.current += 1
    setMain(null)
    setCc([])
    setNote(defaultMessage)
    setError(null)
    setWarning(null)
    setPreviewData(null)
    setSummaryOnly(false)
    setSeededFor(null)
    setCcSeededFor(null)
    preserveHandoffResultRef.current = false
    setPreserveHandoffResult(false)
  }

  const [dialogCaseId, setDialogCaseId] = useState(caseId)
  const caseJustChanged = dialogCaseId !== caseId
  if (caseJustChanged) {
    setDialogCaseId(caseId)
    reset()
  }

  // Seed the main recipient from the Ansvarig recorded earlier. Only ever fills
  // an EMPTY field, and only once per open — a pre-addressed email that quietly
  // overrode a deliberate pick would be the worst failure this dialog has. The
  // pick stays fully editable either way, since the Ansvarig may have changed
  // since the case was created.
  // Skipped on the render where the case changed: reset() has only SCHEDULED
  // main -> null, so seeding here would read the previous case's recipient and
  // mark the new case seeded with an empty field. The next render seeds it.
  const seedKey = open && responsible?.oid ? `${caseId}:${responsible.oid}` : null
  if (!caseJustChanged && seedKey && seededFor !== seedKey) {
    setSeededFor(seedKey)
    if (!main) setMain(responsible)
  }

  // Seed Cc from the case's Deltagare the same way: once per open, only into an
  // empty list, and every row stays removable.
  const participantsQuery = useTenderHandoffCc(caseId, open && !handedOff && !preserveHandoffResult)
  const verifiedCcReady =
    !!participantsQuery.data &&
    !participantsQuery.isFetching &&
    !participantsQuery.isPaused &&
    !participantsQuery.isError
  const ccSeedKey = open && verifiedCcReady ? caseId : null
  if (!caseJustChanged && ccSeedKey && ccSeededFor !== ccSeedKey) {
    setCcSeededFor(ccSeedKey)
    if (cc.length === 0)
      setCc((participantsQuery.data?.recipients ?? []).filter((p) => p.oid !== responsible?.oid))
  }
  const participantsReady = verifiedCcReady && ccSeededFor === caseId
  const canSubmit = !!main && !!main.email && !handoff.isPending && participantsReady

  // Prefill already carries canonical addresses. Sending revalidates every oid.
  const ccRows = cc

  const handleOpenChange = (next: boolean) => {
    if (!next) reset()
    onOpenChange(next)
  }

  const handleSubmit = async () => {
    if (!canSubmit) return
    setError(null)
    setWarning(null)
    // Set the ref before the mutation begins.  React Query invalidates the
    // case on success, so this also covers a very fast refetch.
    preserveHandoffResultRef.current = true
    setPreserveHandoffResult(true)
    try {
      const result = await handoff.mutateAsync({
        mainRecipient: main,
        cc: ccRows,
        note: note.trim() || undefined,
      })
      // The handoff always completes; if the email couldn't go out, keep the
      // dialog open with a warning so the user knows to notify the recipient.
      if (result.emailSent) {
        reset()
        onOpenChange(false)
      } else {
        setWarning(t("tender.handoff.emailFallback"))
      }
    } catch (err) {
      preserveHandoffResultRef.current = false
      setPreserveHandoffResult(false)
      // e.g. the server's "X kan inte ta emot överlämningen" for a guest.
      setError(serverErrorMessage(err, t("tender.handoff.sendError")))
    }
  }

  // Preview renders the email server-side with the same inputs the send would
  // use (recipient name + message); the layout is left to the tenant setting.
  const handlePreview = async () => {
    setError(null)
    const generation = previewGenerationRef.current
    try {
      const result = await preview.mutateAsync({
        mainRecipientName: main?.displayName,
        note: note.trim() || undefined,
      })
      if (previewGenerationRef.current !== generation) return // dialog was reset meanwhile
      setPreviewData(result)
    } catch (err) {
      if (previewGenerationRef.current !== generation) return
      // e.g. the 409 "summary not ready" text from the server.
      setError(serverErrorMessage(err, t("tender.handoff.previewError")))
    }
  }

  if (summaryOnly || (handedOff && !preserveHandoffResult && !preserveHandoffResultRef.current))
    return (
      <TenderSummaryEmailDialog
        caseId={caseId}
        open={open}
        onBack={handedOff ? undefined : () => setSummaryOnly(false)}
        onOpenChange={handleOpenChange}
      />
    )

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ArrowRightLeft className="size-5" />
            {t("tender.handoff.title")}
          </DialogTitle>
          <DialogDescription>{t("tender.handoff.description")}</DialogDescription>
        </DialogHeader>

        <DialogBody className="space-y-5">
          <Button
            className="w-full"
            disabled={busy}
            variant="outline"
            onClick={() => setSummaryOnly(true)}
          >
            <Mail className="mr-2 size-4" />
            {t("tender.summaryEmail.title")}
          </Button>
          {/* Main recipient — becomes the new owner (Ansvarig). */}
          <div className="space-y-2">
            <Label className="flex items-center gap-2 text-sm font-medium">
              <Crown className="text-muted-foreground size-4" />
              {t("tender.handoff.mainRecipientLabel")}
            </Label>
            {main ? (
              <PersonRow
                requireEmail
                icon={<Crown className="text-warning size-4" />}
                person={main}
                onRemove={() => setMain(null)}
              />
            ) : (
              <DirectorySearch
                membersOnly
                disabled={busy}
                placeholder={t("tender.handoff.mainRecipientPlaceholder")}
                scopeGroup={scopeGroup}
                selectedOids={selectedOids}
                types={["users"]}
                onSelect={(r) => setMain(toHandoffRecipient(r))}
              />
            )}
            <p className="text-muted-foreground text-xs">{t("tender.handoff.mainRecipientHint")}</p>
          </div>

          <Separator />

          {/* CC recipients — added as participants. */}
          <div className="space-y-2">
            <Label className="flex items-center gap-2 text-sm font-medium">
              <Users className="text-muted-foreground size-4" />
              {t("tender.handoff.ccLabel")}
            </Label>
            <DirectorySearch
              membersOnly
              disabled={busy || !participantsReady}
              placeholder={t("tender.handoff.ccPlaceholder")}
              scopeGroup={scopeGroup}
              selectedOids={selectedOids}
              types={["users"]}
              onSelect={(r) => setCc((prev) => [...prev, toHandoffRecipient(r)])}
            />
            {!participantsReady &&
              (participantsQuery.isError ? (
                <div className="space-y-2" role="alert">
                  <p className="text-destructive text-sm">
                    {t("tender.handoff.participantsLoadError")}
                  </p>
                  <Button
                    disabled={participantsQuery.isFetching}
                    size="sm"
                    variant="outline"
                    onClick={() => void participantsQuery.refetch()}
                  >
                    {t("common.retry")}
                  </Button>
                </div>
              ) : (
                <p className="text-muted-foreground text-xs" role="status">
                  {t("tender.handoff.participantsLoading")}
                </p>
              ))}
            {participantsReady && !!participantsQuery.data.excluded.length && (
              <p
                className="border-warning bg-warning/10 text-warning-foreground rounded-md border px-3 py-2 text-sm"
                role="status"
              >
                {t("tender.handoff.ccExcluded", {
                  names: participantsQuery.data.excluded.map((p) => p.displayName).join(", "),
                })}
              </p>
            )}
            {ccRows.length > 0 && (
              <div className="space-y-1.5">
                {ccRows.map((r) => (
                  <PersonRow
                    key={r.oid}
                    requireEmail
                    icon={<Users className="text-muted-foreground size-4" />}
                    person={r}
                    onRemove={() => setCc((prev) => prev.filter((x) => x.oid !== r.oid))}
                  />
                ))}
              </div>
            )}
          </div>

          <Separator />

          {/* Intro message — prefilled with the default bid prompt, editable. */}
          <div className="space-y-2">
            <Label className="text-sm font-medium" htmlFor="tender-handoff-note">
              {t("tender.handoff.noteLabel")}
            </Label>
            <Textarea
              disabled={busy}
              id="tender-handoff-note"
              placeholder={t("tender.handoff.notePlaceholder")}
              rows={3}
              value={note}
              onChange={(e) => setNote(e.target.value)}
            />
            <p className="text-muted-foreground text-xs">{t("tender.handoff.noteHint")}</p>
          </div>

          {error && <p className="text-destructive text-sm">{error}</p>}
          {warning && (
            <div className="border-warning bg-warning/10 text-warning-foreground rounded-md border px-3 py-2 text-sm">
              {warning}
            </div>
          )}
        </DialogBody>

        <DialogFooter className="flex-col gap-2 sm:flex-col sm:space-x-0">
          <Button
            className="w-full"
            disabled={!canPreview}
            variant="outline"
            onClick={handlePreview}
          >
            {preview.isPending ? (
              <Loader2 className="size-4 animate-spin" />
            ) : (
              <Eye className="size-4" />
            )}
            {t("tender.handoff.preview")}
          </Button>
          <div className="grid grid-cols-1 gap-2 sm:flex sm:justify-end">
            <Button variant="outline" onClick={() => handleOpenChange(false)}>
              {warning ? t("tender.handoff.close") : t("tender.handoff.cancel")}
            </Button>
            {!warning && (
              <Button disabled={!canSubmit} onClick={handleSubmit}>
                {handoff.isPending ? (
                  <Loader2 className="size-4 animate-spin" />
                ) : (
                  <Mail className="size-4" />
                )}
                {t("tender.handoff.submit")}
              </Button>
            )}
          </div>
        </DialogFooter>

        {/* Nested preview: the full branded email in a fully sandboxed iframe
            (no scripts, opaque origin) so the rendered HTML cannot touch the app.
            The content is a flex column capped at the viewport height; the iframe
            asks for 70vh but may shrink (min-h-0) so the Close button is never
            clipped on low viewports. Deliberately no flex-1: with the dialog's
            auto height that would collapse the iframe to its 150px intrinsic size. */}
        <Dialog open={previewData !== null} onOpenChange={(next) => !next && setPreviewData(null)}>
          <DialogContent className="flex max-w-3xl flex-col">
            <DialogHeader>
              <DialogTitle>{previewData?.subject}</DialogTitle>
              <DialogDescription>{t("tender.handoff.previewTitle")}</DialogDescription>
            </DialogHeader>
            <iframe
              className="h-[70vh] min-h-0 w-full rounded-md border bg-white"
              sandbox=""
              srcDoc={previewData?.html ?? ""}
              title={t("tender.handoff.previewTitle")}
            />
            <DialogFooter>
              <Button variant="outline" onClick={() => setPreviewData(null)}>
                {t("tender.handoff.previewClose")}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </DialogContent>
    </Dialog>
  )
}
